import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { api, setApiContext } from '@/lib/api';
import { APP } from '@/constants/testIds';
import Header from '@/components/Header';
import FarmerFarmSelector from '@/components/FarmerFarmSelector';
import ContextControls from '@/components/ContextControls';
import Landing from '@/components/Landing';
import Dashboard from '@/components/Dashboard';
import Chatbot from '@/components/Chatbot';
import WeatherBar from '@/components/WeatherBar';
import InstallPrompt from '@/components/InstallPrompt';

function App() {
  const [farmers, setFarmers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [crops, setCrops] = useState([]);
  const [activeFarmerId, setActiveFarmerId] = useState(null);
  const [activeFarmId, setActiveFarmId] = useState(null);

  // Context (applies to advisories)
  const [year, setYear] = useState(2026);
  const [season, setSeason] = useState('Kharif');
  const [selectedCrop, setSelectedCrop] = useState('rice');

  useEffect(() => {
    api.listFarmers().then(setFarmers).catch(() => toast.error('Failed to load farmers'));
    api.listCrops().then(setCrops).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch, api is a stable module import

  useEffect(() => {
    if (!activeFarmerId) { setFarms([]); setActiveFarmId(null); return; }
    api.listFarms(activeFarmerId).then((rows) => {
      setFarms(rows);
      // Keep current farm if it belongs to this farmer, else pick the first.
      setActiveFarmId(prev => (rows.find(f => f.id === prev) ? prev : rows[0]?.id ?? null));
    });
  }, [activeFarmerId]);

  const activeFarmer = farmers.find(f => f.id === activeFarmerId) || null;
  const activeFarm = farms.find(f => f.id === activeFarmId) || null;

  // Keep the api client's farm context in sync so runs get logged automatically.
  useEffect(() => {
    setApiContext({ farm_id: activeFarmId, farmer_id: activeFarmerId });
  }, [activeFarmId, activeFarmerId]);

  const refreshFarmers = () => api.listFarmers().then(setFarmers);
  const refreshFarms = () => activeFarmerId && api.listFarms(activeFarmerId).then(setFarms);

  return (
    <div className="min-h-screen bg-icrisat-cream pt-safe pb-[72px] sm:pb-[64px]">
      <BrowserRouter>
        <Header />
        <main className="max-w-[1400px] mx-auto px-3 sm:px-8 py-4 sm:py-8">
          <Routes>
            <Route path="/" element={
              !activeFarmerId ? (
                <Landing
                  farmers={farmers}
                  onSelectFarmer={setActiveFarmerId}
                  onSelectFarm={(farmerId, farmId) => {
                    // Preselect the farm BEFORE farms load; the effect below
                    // preserves prev if it exists in the loaded rows.
                    if (farmId) setActiveFarmId(farmId);
                    setActiveFarmerId(farmerId);
                  }}
                  onFarmerCreated={async () => { await refreshFarmers(); }}
                />
              ) : (
                <>
                  <FarmerFarmSelector
                    farmers={farmers}
                    farms={farms}
                    activeFarmer={activeFarmer}
                    activeFarm={activeFarm}
                    setActiveFarmerId={setActiveFarmerId}
                    setActiveFarmId={setActiveFarmId}
                    onFarmerCreated={refreshFarmers}
                    onFarmCreated={refreshFarms}
                  />
                  {activeFarm && (
                    <>
                      <ContextControls
                        crops={crops}
                        year={year} setYear={setYear}
                        season={season} setSeason={setSeason}
                        selectedCrop={selectedCrop} setSelectedCrop={setSelectedCrop}
                        activeFarm={activeFarm}
                      />
                      <Dashboard
                        farmer={activeFarmer}
                        farm={activeFarm}
                        crops={crops}
                        year={year} season={season} selectedCrop={selectedCrop}
                        setSelectedCrop={setSelectedCrop}
                      />
                    </>
                  )}
                </>
              )
            } />
          </Routes>
        </main>
        <Footer />
        <WeatherBar farm={activeFarm} />
        <Chatbot
          farmer={activeFarmer}
          farm={activeFarm}
          crops={crops}
          selectedCrop={selectedCrop}
          season={season}
        />
        <InstallPrompt />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

const Footer = () => (
  <footer className="mt-16 border-t border-border/60 py-8 px-4 text-center text-sm text-muted-foreground">
    <div className="max-w-[1400px] mx-auto">
      <p className="font-display text-base text-icrisat-green">ICRISAT AgriAgent · For Small Holder Farmers · Telangana</p>
      <p className="mt-2 max-w-3xl mx-auto">
        Decision-support only — not financial or agronomic advice. Always confirm against your local
        KVK / PJTSAU Agromet Advisory (AAS) bulletin before acting on any single recommendation.
      </p>
    </div>
  </footer>
);

export default App;
